/**
 * generateTextPoster.js
 * Generates a poster image (data URL) from text content for text-only posts.
 * Used when minting text-only posts as NFTs — they need an image for the token metadata.
 *
 * Reads the app's --color-surface-canvas token for the background and
 * auto-detects whether to use light or dark text based on luminance.
 */

const CANVAS_SIZE = 1200;
const PADDING = 80;
const MAX_WIDTH = CANVAS_SIZE - PADDING * 2;

/**
 * Parse a hex colour string into { r, g, b }.
 * Supports #RGB, #RRGGBB with optional leading #.
 */
function parseHex(hex) {
  let h = hex.replace(/^#/, "");
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/**
 * Relative luminance (sRGB) — used to decide text colour.
 * Returns 0 (black) to 1 (white).
 */
function luminance({ r, g, b }) {
  const toLinear = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Darken or lighten a hex colour by `amount` (0-255 per channel).
 * Positive = lighten, negative = darken.
 */
function adjustColor(hex, amount) {
  const { r, g, b } = parseHex(hex);
  const clamp = (v) => Math.max(0, Math.min(255, v));
  const nr = clamp(r + amount);
  const ng = clamp(g + amount);
  const nb = clamp(b + amount);
  return `#${((1 << 24) | (nr << 16) | (ng << 8) | nb).toString(16).slice(1)}`;
}

/**
 * Wrap text into lines that fit within maxWidth.
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} maxWidth
 * @returns {string[]}
 */
function wrapText(ctx, text, maxWidth) {
  const lines = [];
  const paragraphs = text.split(/\n/);
  for (const paragraph of paragraphs) {
    if (!paragraph.trim()) {
      lines.push("");
      continue;
    }
    const words = paragraph.split(/\s+/);
    let currentLine = "";
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);
  }
  return lines;
}

/**
 * Generate a poster image data URL from text content.
 *
 * @param {Object} options
 * @param {string} [options.title] - Post title
 * @param {string} [options.body]  - Post body / caption / description
 * @returns {string} data URL (image/png)
 */
export function generateTextPoster({ title = "", body = "" } = {}) {
  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_SIZE;
  canvas.height = CANVAS_SIZE;
  const ctx = canvas.getContext("2d");

  // --- Resolve the canvas background from the semantic CSS token ---
  const rootStyles = getComputedStyle(document.documentElement);
  const bgVar = (rootStyles.getPropertyValue("--color-surface-canvas") || "").trim();
  const bgColor = bgVar || "#2d2d2d"; // fallback dark

  // Subtle gradient using a slightly adjusted shade
  const gradient = ctx.createLinearGradient(0, 0, 0, CANVAS_SIZE);
  gradient.addColorStop(0, bgColor);
  gradient.addColorStop(1, adjustColor(bgColor, -18));
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

  // Determine if background is light or dark for text colour
  const isLight = luminance(parseHex(bgColor)) > 0.45;

  // Choose text colours based on background luminance
  const textPrimary = isLight ? "#1a1a1a" : "#ffffff";
  const textSecondary = isLight ? "rgba(0, 0, 0, 0.65)" : "rgba(255, 255, 255, 0.75)";

  // --- Title ---
  let yOffset = PADDING + 60;
  const titleText = (title || "").trim();
  const bodyText = (body || "").trim();

  if (titleText) {
    ctx.font = "bold 56px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    ctx.fillStyle = textPrimary;
    ctx.textBaseline = "top";
    const titleLines = wrapText(ctx, titleText, MAX_WIDTH);
    const visibleTitleLines = titleLines.slice(0, 4);
    for (const line of visibleTitleLines) {
      ctx.fillText(line, PADDING, yOffset);
      yOffset += 68;
    }
    if (titleLines.length > 4) {
      const lastLine = visibleTitleLines[visibleTitleLines.length - 1];
      const truncated = lastLine.length > 3 ? lastLine.slice(0, -3) + "…" : lastLine;
      // Re-draw last line with ellipsis (clear using bg colour)
      ctx.fillStyle = bgColor;
      ctx.fillRect(PADDING, yOffset - 68, MAX_WIDTH, 68);
      ctx.fillStyle = textPrimary;
      ctx.fillText(truncated, PADDING, yOffset - 68);
    }
    yOffset += 20;
  }

  // --- Body ---
  if (bodyText) {
    ctx.font = "36px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    ctx.fillStyle = textSecondary;
    ctx.textBaseline = "top";
    const bodyLines = wrapText(ctx, bodyText, MAX_WIDTH);
    const lineHeight = 48;
    const remainingHeight = CANVAS_SIZE - yOffset - PADDING;
    const maxLines = Math.max(1, Math.floor(remainingHeight / lineHeight));
    const visibleBodyLines = bodyLines.slice(0, maxLines);
    for (let i = 0; i < visibleBodyLines.length; i++) {
      let line = visibleBodyLines[i];
      if (i === visibleBodyLines.length - 1 && bodyLines.length > maxLines) {
        line = line.length > 3 ? line.slice(0, -3) + "…" : line + "…";
      }
      ctx.fillText(line, PADDING, yOffset);
      yOffset += lineHeight;
    }
  }

  return canvas.toDataURL("image/png");
}
